import { useQuery } from '@tanstack/react-query'
import { topicsApi } from '../api/topics'
import type { TopicInfo } from '../types/api'
import { useWorkspacesQuery } from './queries'

/**
 * Topics for a workspace, gated on the caller actually being a member.
 *
 * A persisted `activeWorkspaceId` can be stale (a previous user on this browser,
 * or a revoked membership); querying its topics then 403s, which the global
 * error handler surfaces as a spurious toast. Gating every topics fetch on
 * membership in one place stops call sites from drifting apart on this guard
 * (they previously did — ChatPage omitted it while the sidebar had it).
 */
export function useTopicsQuery(
  workspaceId: string | null | undefined,
  opts?: { enabled?: boolean },
) {
  const { data: workspaces = [] } = useWorkspacesQuery()
  const isMember = workspaces.some((w) => w.workspace_id === workspaceId)

  return useQuery<TopicInfo[]>({
    queryKey: ['topics', workspaceId],
    queryFn: () =>
      workspaceId
        ? topicsApi.listForWorkspace(workspaceId)
        : Promise.resolve([]),
    enabled: !!workspaceId && isMember && (opts?.enabled ?? true),
    staleTime: 30_000,
  })
}
